/**
 * api.js — Centralized API endpoint definitions.
 *
 * All functions return an Axios response promise: { data, status, headers, ... }.
 * The actual payload is in response.data. Callers must do `const res = await api.xyz(); res.data`.
 *
 * AUTH: Every request goes through apiClient.js interceptors which attach the Bearer token
 * and handle 401 refresh/retry automatically. No auth logic needed here.
 *
 * CACHE BEHAVIOR:
 *   - getCached(key, fetcher): in-memory cache with 60s TTL. Returns stale data within TTL
 *     without hitting the server. Cache is per-page-session (cleared on full page reload).
 *   - clearCache(pattern): deletes all cache entries whose key includes the pattern string.
 *   - Cached endpoints: getParts (key: "parts-{projectId}-{subsystemId}"),
 *     getSystemHierarchy (key: "hierarchy").
 *   - Mutating endpoints (createPart, updatePart, deletePart, createSubsystem,
 *     updateSubsystem, deleteSubsystem) call clearCache() after success.
 *   - Side-effect: if you add a new read endpoint that depends on parts or hierarchy data,
 *     you must either cache-bust it in the relevant mutators or skip caching.
 *   - Side-effect: clearCache('parts') wipes ALL parts cache entries across all
 *     project/subsystem combinations, not just the one that changed.
 *
 * RESPONSE FORMATS (all wrapped in Axios response — access via .data):
 *   - List endpoints: { items: [...] } or bare [...] depending on the backend route.
 *   - Single-entity endpoints: the entity object directly.
 *   - Mutating endpoints: the created/updated entity or { message: "..." }.
 *   - File/download endpoints: { url: "..." } (presigned S3 URL).
 *   - Streaming endpoints (invokeOrchestrator): response is a ReadableStream.
 *   Check the backend route for the exact shape; this file does not transform responses.
 */
import client from './apiClient';

// All endpoints are prefixed with this. Relative to apiClient's baseURL.
// Side-effect: changing this breaks every endpoint if the backend prefix differs.
const API_BASE_URL = '/api/v1'; // Relative to client baseURL

// ─── In-Memory Cache ────────────────────────────────────────────────────────
// Simple TTL cache. Not shared across tabs. Cleared on page reload.
// Only used by getParts and getSystemHierarchy to reduce redundant fetches
// when navigating between views that display the same data.
const cache = {};
const CACHE_TTL = 60000; // 1 minute

/**
 * Return cached data if fresh; otherwise execute fetcher and cache the result.
 * NOTE: The entire Axios response object is cached (not just .data).
 * If two components call the same cached endpoint simultaneously before the first
 * resolves, both will make network requests (no in-flight dedup).
 */
const getCached = async (key, fetcher) => {
    const now = Date.now();
    if (cache[key] && (now - cache[key].timestamp < CACHE_TTL)) {
        return cache[key].data;
    }
    const response = await fetcher();
    cache[key] = { timestamp: now, data: response };
    return response;
};

/**
 * Delete all cache entries whose key contains the given pattern string.
 * Example: clearCache('parts') removes "parts-proj1-sub1", "parts-proj2-sub2", etc.
 * Example: clearCache('hierarchy') removes the single "hierarchy" entry.
 */
const clearCache = (pattern) => {
    Object.keys(cache).forEach(key => {
        if (key.includes(pattern)) {
            delete cache[key];
        }
    });
};

export const api = {

    // ═══════════════════════════════════════════════════════════════════════════
    // USERS & GROUPS
    // Response: getUsers → { users: [...] }, createUser/createGroup → created entity
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: App.js (loads user list on mount), ACLManager (user picker for permissions)
    getUsers: () => client.get(`${API_BASE_URL}/auth/users`),
    // Called by: (admin flows — not directly referenced in current components)
    createUser: (userData) => client.post(`${API_BASE_URL}/users`, userData),
    // Called by: (admin flows — not directly referenced in current components)
    createGroup: (groupData) => client.post(`${API_BASE_URL}/groups`, groupData),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROJECTS
    // Response: getProjects → [...projects], getProject → single project object
    // Side-effect: deleteProject cascades on the backend (branches, files, etc.)
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: App.js (project selector), ProjectDashboard, ProductPortfolioView
    getProjects: () => client.get(`${API_BASE_URL}/projects`),
    // Called by: ProjectView (loads project details)
    getProject: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}`),
    // Called by: ProjectDashboard (new project dialog)
    createProject: (projectData) => client.post(`${API_BASE_URL}/projects`, projectData),
    // Called by: ProjectView (inline edit of project name/description)
    updateProject: (projectId, projectData) => client.put(`${API_BASE_URL}/projects/${projectId}`, projectData),
    // Called by: ProjectDashboard (delete project action)
    deleteProject: (projectId, userId) => client.delete(`${API_BASE_URL}/projects/${projectId}`, { params: { user_id: userId } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // BRANCHES
    // Response: getBranches → [...branches], each branch has { branch_id, status, ... }
    // Side-effect: deleteBranch is irreversible; all uncommitted changes on it are lost.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: App.js (branch selector), BranchesView, DocumentsView, CADModelsView, UnifiedCADView
    getBranches: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/branches`),
    // Called by: BranchesView (create branch dialog)
    createBranch: (projectId, branchData) => client.post(`${API_BASE_URL}/projects/${projectId}/branches`, branchData),
    // Called by: DocumentsView, CADModelsView, CategoryFileView, UnifiedCADView (file listing by branch)
    getBranchFiles: (projectId, branchId, fileType) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/files`,
        { params: { file_type: fileType } }
    ),
    // Called by: BranchesView (delete branch action)
    deleteBranch: (projectId, branchId, userId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}`,
        { params: { user_id: userId } }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // VERSION CONTROL — Staging & Commits
    // These endpoints manage the git-like staging area for file changes.
    // Note: projectId is typically extracted from branchId format "PROJECT_ID-branch_name".
    // Response: getPendingChanges → { staged: [...], unstaged: [...] }
    //           commitChanges → { commit_id, message, ... }
    // Side-effect: commitChanges finalizes staged changes; they become immutable.
    // Side-effect: discardAllChanges permanently drops all unstaged/staged changes.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: PendingChangesPanel (loads pending changes for the current branch)
    getPendingChanges: (projectId, branchId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/pending`
    ),
    // Called by: (staging is typically triggered from file upload/edit flows)
    stageFile: (projectId, branchId, fileId, versionId, fileName) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/stage`,
        { file_id: fileId, version_id: versionId, file_name: fileName }
    ),
    // Called by: (staging a file deletion before commit)
    stageFileDeletion: (projectId, branchId, fileId, fileName) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/stage-delete`,
        { file_id: fileId, file_name: fileName }
    ),
    // Called by: PendingChangesPanel (unstage action per file)
    unstageFile: (projectId, branchId, fileId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/stage/${fileId}`
    ),
    // Called by: (re-staging a specific version of a file)
    restageVersion: (projectId, branchId, versionId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/restage/${versionId}`
    ),
    // Called by: PendingChangesPanel (commit button)
    commitChanges: (projectId, branchId, message, authorName, authorEmail, milestoneId, milestoneProgress) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/commits`,
        { message, author_name: authorName, author_email: authorEmail, milestone_id: milestoneId || null, milestone_progress: milestoneProgress ?? null }
    ),
    // Called by: PendingChangesPanel (discard all changes button)
    discardAllChanges: (projectId, branchId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/pending`
    ),
    // Called by: BranchDiffDialog (compare branches)
    getBranchDiff: (projectId, branchId, compareTo) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/diff`,
        { params: { compare_to: compareTo } }
    ),
    // Called by: CommitHistory (commit log), TagManager (commit picker for tags)
    getCommitHistory: (projectId, branchId, limit = 50) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/commits`,
        { params: { limit } }
    ),
    // Called by: CommitHistory (expand commit to see file diffs)
    getCommitDiff: (projectId, commitId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/commits/${commitId}/diff`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // MERGE OPERATIONS
    // Response: previewMerge → { conflicts: [...], changes: [...] }
    //           createMergeRequest → { merge_request_id, ... }
    //           executeMerge → { commit_id, ... }
    // Side-effect: executeMerge creates a merge commit on the target branch.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: MergeDialog (preview step)
    previewMerge: (projectId, sourceBranchId, targetBranchId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${sourceBranchId}/merge-preview`,
        null,
        { params: { target_branch_id: targetBranchId } }
    ),
    // Called by: MergeDialog (create MR step)
    createMergeRequest: (projectId, sourceBranchId, targetBranchId, title, description, ecoId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${sourceBranchId}/merge-requests`,
        { target_branch_id: targetBranchId, title, description, eco_id: ecoId }
    ),
    // Called by: MergeDialog (fetch conflicts after MR creation)
    getMergeConflicts: (projectId, branchId, mergeRequestId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/merge-requests/${mergeRequestId}/conflicts`
    ),
    // Called by: MergeDialog (execute merge with optional conflict resolutions)
    executeMerge: (projectId, branchId, mergeRequestId, conflictResolutions) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/merge-requests/${mergeRequestId}/execute`,
        { conflict_resolutions: conflictResolutions }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // REBASE OPERATIONS
    // Response: previewRebase → { changes: [...], conflicts: [...] }
    //           executeRebase → { commit_id, ... }
    // Side-effect: executeRebase rewrites the branch's commit history.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (rebase UI — not directly referenced in current components)
    previewRebase: (projectId, branchId, ontoBranchId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/rebase-preview`,
        { onto_branch_id: ontoBranchId }
    ),
    executeRebase: (projectId, branchId, ontoBranchId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/rebase`,
        { onto_branch_id: ontoBranchId }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // CHERRY-PICK & REVERT
    // Side-effect: cherryPick creates a new commit on the target branch.
    // Side-effect: revertCommit creates a new "undo" commit — does not delete history.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: CommitHistory (cherry-pick action on a commit row)
    cherryPick: (projectId, branchId, commitId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/cherry-pick`,
        { commit_id: commitId }
    ),
    // Called by: CommitHistory (revert action on a commit row)
    revertCommit: (projectId, branchId, commitId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/revert`,
        null,
        { params: { commit_id: commitId } }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // TAGS
    // Response: getTags → [...tags], each tag has { tag_id, tag_name, tag_type, commit_id }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: TagManager (create tag dialog)
    createTag: (projectId, branchId, commitId, tagName, tagType, message) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/tags`,
        { commit_id: commitId, tag_name: tagName, tag_type: tagType, message }
    ),
    // Called by: TagManager (tag listing)
    getTags: (projectId, branchId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/tags`
    ),
    // Called by: TagManager (delete tag action)
    deleteTag: (projectId, branchId, tagId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/tags/${tagId}`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // RELEASES
    // Response: getReleases → [...releases], createRelease → release object
    // Side-effect: publishRelease makes a release immutable and visible to all users.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ReleaseManager (create release form)
    createRelease: (projectId, tagId, version, title, description, releaseNotes) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/releases`,
        { tag_id: tagId, version, title, description, release_notes: releaseNotes }
    ),
    // Called by: ReleaseManager (release listing, includeDrafts=true)
    getReleases: (projectId, includeDrafts = false) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/releases`,
        { params: { include_drafts: includeDrafts } }
    ),
    // Called by: ReleaseManager (publish action)
    publishRelease: (projectId, releaseId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/releases/${releaseId}/publish`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // EXPERIMENTS
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (not directly referenced in current components)
    createExperiment: (experimentData) => client.post(`${API_BASE_URL}/experiments`, experimentData),

    // ═══════════════════════════════════════════════════════════════════════════
    // PERMISSIONS
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (not directly referenced in current components)
    requestPermission: (requestData) => client.post(`${API_BASE_URL}/permissions/request`, requestData),

    // ═══════════════════════════════════════════════════════════════════════════
    // ACL (Access Control List)
    // Response: getMyPermission → { permission_level: "owner"|"editor"|"viewer"|... }
    //           getProjectPermissions → [...acl entries]
    // Side-effect: grantPermission/updatePermission/revokePermission changes who can
    //   access the project. This affects all project-scoped operations.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ACLManager (determines what UI controls to show)
    getMyPermission: (projectId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/acl/my-permission`
    ),
    // Called by: ACLManager (permission table)
    getProjectPermissions: (projectId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/acl`
    ),
    // Called by: ACLManager (grant permission to a user)
    grantPermission: (projectId, userId, permissionLevel, expiresAt = null) => client.post(
        `${API_BASE_URL}/projects/${projectId}/acl`,
        { user_id: userId, permission_level: permissionLevel, expires_at: expiresAt }
    ),
    // Called by: ACLManager (edit permission level)
    updatePermission: (projectId, userId, permissionLevel, expiresAt = null) => client.put(
        `${API_BASE_URL}/projects/${projectId}/acl/${userId}`,
        { user_id: userId, permission_level: permissionLevel, expires_at: expiresAt }
    ),
    // Called by: ACLManager (revoke access)
    revokePermission: (projectId, userId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/acl/${userId}`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // FILE MANAGEMENT (S3-based files — CAD models, simulation results)
    // These endpoints deal with raw S3 files (upload/download/delete).
    // Response: uploadFile → { document_id, s3_key, ... }, getS3Files → [...files]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: FileUpload, CADModelsView, SimulationsView, UnifiedCADView (file upload)
    // NOTE: Content-Type is set to undefined to let the browser set multipart boundary.
    uploadFile: (formData, onProgress) => {
        const file = formData.get('file');
        const sizeMB = file ? file.size / (1024 * 1024) : 0;
        const timeout = Math.max(120000, sizeMB * 10000); // min 2min, +10s per MB
        return client.post(`${API_BASE_URL}/files/upload`, formData, {
            headers: { 'Content-Type': undefined },
            onUploadProgress: onProgress,
            timeout,
        });
    },
    // Called by: (S3-based file listing — used alongside getDocuments in some views)
    getS3Files: (projectId, fileType) => client.get(`${API_BASE_URL}/files`, { params: { project_id: projectId, file_type: fileType } }),
    // S3-specific file deletion (distinct from document-based deleteFile below)
    deleteS3File: (fileId) => client.delete(`${API_BASE_URL}/files/${fileId}`),
    updateS3FileDescription: (filename, description) => client.put(`${API_BASE_URL}/files/${encodeURIComponent(filename)}/description`, { description }),
    // Called by: VersionHistoryPanel (load version list for a file)
    getFileVersionHistory: (fileId) => client.get(`${API_BASE_URL}/files/${fileId}/versions`),
    // Called by: (version comparison UI)
    compareVersions: (version1Id, version2Id, comparisonType = 'combined') => client.post(`${API_BASE_URL}/versions/compare`, {
        version1_id: version1Id,
        version2_id: version2Id,
        comparison_type: comparisonType
    }),

    // ═══════════════════════════════════════════════════════════════════════════
    // FILE MAINTENANCE (cleanup orphaned S3 objects / DB records)
    // Response: findOrphanedFiles → { orphaned_s3: [...], orphaned_db: [...] }
    // Side-effect: cleanupOrphanedFiles permanently deletes files. Irreversible.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (admin/maintenance UI — not directly referenced in current components)
    findOrphanedFiles: () => client.get(`${API_BASE_URL}/files/maintenance/orphans`),
    cleanupOrphanedFiles: (cleanupS3 = true, cleanupDb = true) =>
        client.post(`${API_BASE_URL}/files/maintenance/cleanup`, null, { params: { cleanup_s3: cleanupS3, cleanup_db: cleanupDb } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENT-BASED FILES (from database — for chat, documents view)
    // These use the /documents endpoint (DB-backed metadata + S3 storage).
    // Response: getFiles → [...documents], getDocument → single document object
    //           downloadFile/downloadVersion → { url: "presigned-s3-url" }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ChatContainer (fetches files for context), DocumentsView
    getFiles: (projectId, fileType) => client.get(`${API_BASE_URL}/documents`, { params: { project_id: projectId, type: fileType } }),
    // Called by: FileDetailDrawer, CADFileDetailDrawer (load document metadata)
    getDocument: (documentId) => client.get(`${API_BASE_URL}/documents/${documentId}`),
    // Called by: DocumentsView, CADModelsView, SimulationsView, UnifiedCADView,
    //           FileDetailDrawer (delete document action)
    deleteFile: (documentId) => client.delete(`${API_BASE_URL}/documents/${documentId}`),
    // Called by: (update description inline)
    updateFileDescription: (documentId, description) => client.put(`${API_BASE_URL}/documents/${documentId}`, { description }),
    // Called by: (download action — returns presigned URL)
    downloadFile: (documentId) => client.get(`${API_BASE_URL}/documents/${documentId}/download`),
    // Called by: VersionHistoryPanel (download a specific version)
    downloadVersion: (versionId) => client.get(`${API_BASE_URL}/versions/${versionId}/download`),
    // Called by: (remove a file from a branch's file list)
    removeBranchFile: (projectId, branchId, fileId, userId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/files/${fileId}`,
        { params: { user_id: userId } }
    ),
    // Called by: CategoryFileView (update file category/metadata)
    updateFileMetadata: (projectId, branchId, fileId, metadata, userId) => client.patch(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/files/${fileId}/metadata`,
        metadata,
        { params: { user_id: userId } }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // PARTS & BOMs
    // CACHE: getParts is cached with key "parts-{projectId}-{subsystemId}" (60s TTL).
    //   createPart, updatePart, deletePart all clear both "parts" and "hierarchy" caches.
    // Response: getParts → [...parts], getPart → single part with BOM children
    //           diffBOM → { added: [...], removed: [...], changed: [...] }
    // Side-effect: deletePart may cascade to BOM relationships and linked documents.
    // Side-effect: createPart/updatePart/deletePart invalidate BOTH "parts" and "hierarchy"
    //   caches, which affects getParts and getSystemHierarchy return values.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: PartsView, AssemblyView, FileUpload, SimulationsView, SubsystemDetailView,
    //           AssemblyDetailView (load parts list)
    getParts: (projectId, subsystemId) => getCached(
        `parts-${projectId}-${subsystemId}`,
        () => client.get(`${API_BASE_URL}/parts`, { params: { project_id: projectId, subsystem_id: subsystemId } })
    ),
    // Called by: AssemblyDetailView (load child part details)
    getPart: (partId) => client.get(`${API_BASE_URL}/parts/${partId}`),
    // Called by: PartsView (new part form), NewAssemblyDialog
    // Cache: clears "parts" + "hierarchy" on success
    createPart: async (partData, parentAssemblyId) => {
        const res = await client.post(`${API_BASE_URL}/parts`, partData, { params: { parent_assembly_id: parentAssemblyId } });
        clearCache('parts'); // Invalidate parts cache
        clearCache('hierarchy');
        return res;
    },
    // Called by: PartsView (edit part form), NewAssemblyDialog (edit mode)
    // Cache: clears "parts" + "hierarchy" on success
    updatePart: async (partId, partData) => {
        const res = await client.put(`${API_BASE_URL}/parts/${partId}`, partData);
        clearCache('parts');
        clearCache('hierarchy');
        return res;
    },
    // Called by: PartsView (delete part action), AssemblyView (delete assembly)
    // Cache: clears "parts" + "hierarchy" on success
    deletePart: async (partId) => {
        const res = await client.delete(`${API_BASE_URL}/parts/${partId}`);
        clearCache('parts');
        clearCache('hierarchy');
        return res;
    },
    // Called by: (BOM diff comparison UI)
    diffBOM: (partId, compareToPartId) => client.post(`${API_BASE_URL}/parts/${partId}/bom/diff`, null, { params: { compare_to_part_id: compareToPartId } }),
    // Called by: (where-used analysis for a part)
    getPartWhereUsed: (partId) => client.get(`${API_BASE_URL}/parts/${partId}/where-used`),

    // ═══════════════════════════════════════════════════════════════════════════
    // SIMULATIONS
    // Response: getSimulations → [...simulations], getSimulation → single simulation
    //           searchSimulations → [...matching simulations]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: SimulationsView (simulation listing)
    getSimulations: (projectId) => client.get(`${API_BASE_URL}/simulations`, { params: { project_id: projectId } }),
    // Called by: (simulation detail view)
    getSimulation: (simId) => client.get(`${API_BASE_URL}/simulations/${simId}`),
    // Called by: SimulationsView (create simulation form)
    createSimulation: (simData) => client.post(`${API_BASE_URL}/simulations`, simData),
    // Called by: SimulationsView (search box)
    searchSimulations: (query, projectId) => client.post(`${API_BASE_URL}/simulations/search`, null, { params: { query, project_id: projectId } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // PRODUCT STRUCTURE (PBS — Product Breakdown Structure)
    // CACHE: getSystemHierarchy is cached with key "hierarchy" (60s TTL).
    //   createSubsystem, updateSubsystem, deleteSubsystem clear "hierarchy" cache.
    //   createPart, updatePart, deletePart also clear "hierarchy" cache.
    // Response: getProductTree → nested tree { id, children: [...] }
    //           getSystemHierarchy → { systems: [{ subsystems: [{ parts: [...] }] }] }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ProductStructureView (tree rendering)
    getProductTree: (rootPartId) => client.get(`${API_BASE_URL}/product-structure/tree/${rootPartId}`),
    // Called by: ProductStructureView (node click → detail panel)
    getNodeDetails: (partId) => client.get(`${API_BASE_URL}/product-structure/node/${partId}`),
    // Called by: PartsView, NewAssemblyDialog, NewSubsystemDialog (hierarchy picker/display)
    // Cache: key "hierarchy", 60s TTL. Invalidated by part and subsystem mutations.
    getSystemHierarchy: () => getCached(
        'hierarchy',
        () => client.get(`${API_BASE_URL}/product-structure/hierarchy`)
    ),
    // Called by: SystemView (system listing)
    getSystems: (projectId) => client.get(`${API_BASE_URL}/product-structure/systems`, { params: { project_id: projectId } }),
    // Called by: SubsystemView (subsystem listing under a system)
    getSubsystems: (systemId, projectId) => client.get(`${API_BASE_URL}/product-structure/subsystems`, { params: { system_id: systemId, project_id: projectId } }),
    // Called by: NewSubsystemDialog (edit mode)
    // Cache: clears "hierarchy" on success
    createSubsystem: async (subsystemData) => {
        const res = await client.post(`${API_BASE_URL}/product-structure/subsystems`, subsystemData);
        clearCache('hierarchy');
        return res;
    },
    // Called by: NewSubsystemDialog (edit mode)
    // Cache: clears "hierarchy" on success
    updateSubsystem: async (subsystemId, subsystemData) => {
        const res = await client.put(`${API_BASE_URL}/product-structure/subsystems/${subsystemId}`, subsystemData);
        clearCache('hierarchy');
        return res;
    },
    // Called by: SubsystemView (delete action), PartsView (delete subsystem action)
    // Cache: clears "hierarchy" on success
    deleteSubsystem: async (subsystemId) => {
        const res = await client.delete(`${API_BASE_URL}/product-structure/subsystems/${subsystemId}`);
        clearCache('hierarchy');
        return res;
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // INTERFACES (connections between subsystems)
    // Response: getInterfaces → [...interfaces]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: SubsystemDetailView (interface listing)
    getInterfaces: (subsystemId) => client.get(`${API_BASE_URL}/product-structure/interfaces`, { params: { subsystem_id: subsystemId } }),
    // Called by: NewInterfaceDialog, SubsystemDetailView (quick-add interface)
    createInterface: (interfaceData) => client.post(`${API_BASE_URL}/product-structure/interfaces`, interfaceData),

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENTS (engineering documents linked to parts/branches)
    // Response: getDocuments → [...documents], getDocument → single document
    //           getDocumentDownloadUrl → { url: "presigned-s3-url" }
    // Side-effect: deleteDocument removes the document and all its versions.
    // Side-effect: uploadDocumentVersion creates a new version; the previous version
    //   remains accessible via version history.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: DocumentsView (main doc listing), DocumentGenerationView, SimulationsView
    getDocuments: (linkedPartId, type, projectId, branchId, { search, limit, offset } = {}) => client.get(`${API_BASE_URL}/documents`, { params: { linked_part_id: linkedPartId, type, project_id: projectId, branch_id: branchId, search, limit, offset } }),
    // Called by: FileDetailDrawer, CADFileDetailDrawer (load document details)
    getDocument: (documentId) => client.get(`${API_BASE_URL}/documents/${documentId}`),
    // Called by: DocumentsView (download button)
    getDocumentDownloadUrl: (documentId) => client.get(`${API_BASE_URL}/documents/${documentId}/download`),
    // Called by: DocumentsView (upload new document form)
    // NOTE: Content-Type is undefined to let the browser set multipart boundary.
    createDocument: (formData) => client.post(`${API_BASE_URL}/documents`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DocumentsView (edit document metadata), CADModelsView (update description)
    updateDocument: (documentId, docData, branchId) => client.put(`${API_BASE_URL}/documents/${documentId}`, docData, { params: { branch_id: branchId } }),
    // Called by: DocumentsView, CADModelsView, UnifiedCADView (upload new version of existing doc)
    uploadDocumentVersion: (documentId, formData) => client.post(`${API_BASE_URL}/documents/${documentId}/versions`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DocumentsView (delete document action)
    deleteDocument: (documentId) => client.delete(`${API_BASE_URL}/documents/${documentId}`),
    // Called by: DocumentsView (diff tab — AI comparison of two documents)
    diffDocuments: (documentIdA, documentIdB) => client.post(`${API_BASE_URL}/documents/diff`, { document_id_a: documentIdA, document_id_b: documentIdB }, { timeout: 120000 }),
    // Called by: UnifiedCADView (fallback for CAD drawings not in documents table)
    deleteDrawing: (drawingId) => client.delete(`${API_BASE_URL}/cad-agent/drawings/${drawingId}`),
    // Called by: delete confirmation dialogs — preview linked records before deletion
    getDeletePreview: (entityType, entityId) => client.get(`${API_BASE_URL}/processing/delete-preview/${entityType}/${entityId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // REQUIREMENTS
    // Response: getRequirements → [...requirements]
    //           getRequirementDownloadUrl → { url: "presigned-s3-url" }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: RequirementsView (requirements listing, filtered by linked part)
    getRequirements: (linkedPartId, status, { search, limit, offset, project_id, branch_id } = {}) => client.get(`${API_BASE_URL}/requirements`, { params: { linked_part_id: linkedPartId, status, search, limit, offset, project_id, branch_id } }),
    // Called by: RequirementsView (create requirement with attachment)
    createRequirement: (formData) => client.post(`${API_BASE_URL}/requirements`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DropZoneUploader (drag-drop in RequirementsView creates a Requirement per file)
    createRequirementFromFile: (formData) => client.post(`${API_BASE_URL}/requirements`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: RequirementsView (edit requirement inline)
    updateRequirement: (reqId, reqData) => client.put(`${API_BASE_URL}/requirements/${reqId}`, reqData),
    // Called by: RequirementsView (delete requirement)
    deleteRequirement: (reqId) => client.delete(`${API_BASE_URL}/requirements/${reqId}`),
    // Called by: RequirementsView (download attachment)
    getRequirementDownloadUrl: (reqId) => client.get(`${API_BASE_URL}/requirements/${reqId}/download`),
    // Called by: RequirementsView (AI extraction from document)
    extractRequirementsFromDocument: (sourceFileId, projectId) => client.post(
        `${API_BASE_URL}/requirements/extract`,
        { source_file_id: sourceFileId, project_id: projectId }
    ),
    // Called by: RequirementsView (bulk import after AI extraction review)
    bulkCreateRequirements: (projectId, requirements) => client.post(
        `${API_BASE_URL}/requirements/bulk`,
        { project_id: projectId, requirements }
    ),
    // Called by: RequirementDetailDrawer (history tab)
    getRequirementVersions: (reqId) => client.get(`${API_BASE_URL}/requirements/${reqId}/versions`),
    // Called by: RequirementDetailDrawer (compare tab)
    compareRequirementVersions: (v1Id, v2Id) => client.post(`${API_BASE_URL}/requirements/versions/compare`, {
        version1_id: v1Id,
        version2_id: v2Id,
        entity_type: 'requirement'
    }),
    // Called by: RequirementsView (coverage dashboard header, verdict/coverage columns)
    getRequirementsSummary: (projectId) => client.get(`${API_BASE_URL}/requirements/summary`, { params: { project_id: projectId } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // ENTITY LINKS (Engineering Ontology)
    // Response: getEntityLinks → {links: [...], total: number}
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: RequirementsView, detail drawers, graph view
    getEntityLinks: (projectId, { entityId, entityType, linkType } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entity-links`, {
            params: { entity_id: entityId, entity_type: entityType, link_type: linkType },
        }),
    createEntityLink: (projectId, data) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links`, data),
    createEntityLinksBulk: (projectId, links) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links/bulk`, { links }),
    deleteEntityLink: (projectId, linkId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/entity-links/${linkId}`),
    getEntityTimeline: (projectId, entityId, limit = 100) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entity-links/timeline`, {
            params: { entity_id: entityId, limit },
        }),
    invalidateEntityLink: (projectId, linkId, { endedAt, supersededBy } = {}) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links/${linkId}/invalidate`, null, {
            params: { ended_at: endedAt, superseded_by: supersededBy },
        }),

    // ═══════════════════════════════════════════════════════════════════════════
    // DECISION JOURNAL
    // ═══════════════════════════════════════════════════════════════════════════
    getDecisions: (projectId, { entityId, decisionType, tag, status, limit, offset } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/decisions`, {
            params: { entity_id: entityId, decision_type: decisionType, tag, status, limit, offset },
        }),
    getDecision: (projectId, entryId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/decisions/${entryId}`),
    createDecision: (projectId, data) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/decisions`, data),
    deleteDecision: (projectId, entryId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/decisions/${entryId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // RECENTLY DELETED (Soft-delete recovery)
    // ═══════════════════════════════════════════════════════════════════════════
    getRecentlyDeleted: (projectId) =>
        client.get(`${API_BASE_URL}/processing/recently-deleted/${projectId}`),
    restoreEntity: (entityType, entityId) =>
        client.post(`${API_BASE_URL}/processing/restore/${entityType}/${entityId}`),
    permanentlyDeleteEntity: (entityType, entityId) =>
        client.delete(`${API_BASE_URL}/processing/permanently-delete/${entityType}/${entityId}`),
    checkDuplicates: (filenames, projectId, branchId, entityType = 'document') =>
        client.post(`${API_BASE_URL}/files/check-duplicates`, { filenames, project_id: projectId, branch_id: branchId, entity_type: entityType }),

    // ═══════════════════════════════════════════════════════════════════════════
    // ENGINEERING MANAGEMENT (Risks, Design Gates, Test Cases)
    // ═══════════════════════════════════════════════════════════════════════════
    getRisks: (projectId, status) => client.get(`${API_BASE_URL}/projects/${projectId}/risks`, { params: { status } }),
    createRisk: (projectId, data) => client.post(`${API_BASE_URL}/projects/${projectId}/risks`, data),
    updateRisk: (projectId, riskId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/risks/${riskId}`, data),
    deleteRisk: (projectId, riskId) => client.delete(`${API_BASE_URL}/projects/${projectId}/risks/${riskId}`),

    getDesignGates: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/design-gates`),
    updateDesignGate: (projectId, gateId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/design-gates/${gateId}`, data),

    getTestCases: (projectId, { status, linked_requirement_id } = {}) => client.get(`${API_BASE_URL}/projects/${projectId}/test-cases`, { params: { status, linked_requirement_id } }),
    createTestCase: (projectId, data) => client.post(`${API_BASE_URL}/projects/${projectId}/test-cases`, data),
    updateTestCase: (projectId, testId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/test-cases/${testId}`, data),
    deleteTestCase: (projectId, testId) => client.delete(`${API_BASE_URL}/projects/${projectId}/test-cases/${testId}`),

    // Inspection Checklists
    getInspectionChecklists: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/inspection-checklists`),
    updateInspectionChecklist: (projectId, checklistId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/inspection-checklists/${checklistId}`, data),
    deleteInspectionChecklist: (projectId, checklistId) => client.delete(`${API_BASE_URL}/projects/${projectId}/inspection-checklists/${checklistId}`),

    // Verification Matrix
    getLatestVerificationMatrix: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/verification-matrix/latest`),

    // AI Verification
    aiGenerateTestCases: (projectId, requirementIds) => client.post(`${API_BASE_URL}/projects/${projectId}/ai/generate-test-cases`, { requirement_ids: requirementIds }),
    aiGenerateInspectionChecklist: (projectId, drawingId, requirementIds) => client.post(`${API_BASE_URL}/projects/${projectId}/ai/generate-inspection-checklist`, { drawing_id: drawingId, requirement_ids: requirementIds }),
    aiAutoMapMatrix: (projectId) => client.post(`${API_BASE_URL}/projects/${projectId}/ai/auto-map-matrix`),
    aiAnalyzeFailure: (projectId, testId) => client.post(`${API_BASE_URL}/projects/${projectId}/ai/analyze-failure`, { test_id: testId }),

    // ═══════════════════════════════════════════════════════════════════════════
    // CHANGE MANAGEMENT (ECR — Engineering Change Request / ECO — Engineering Change Order)
    // Response: getECRs/getECOs → [...items], filtered by status
    // Side-effect: approveECO transitions the ECO to approved state, which may
    //   unlock merge operations that reference the ECO.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (ECR listing UI — not directly referenced beyond admin flows)
    getECRs: (status) => client.get(`${API_BASE_URL}/ecrs`, { params: { status } }),
    createECR: (formData) => client.post(`${API_BASE_URL}/ecrs`, formData, { headers: { 'Content-Type': undefined } }),
    updateECR: (ecrId, data) => client.put(`${API_BASE_URL}/ecrs/${ecrId}`, data),

    // Called by: FileUpload (fetches approved ECOs for linking)
    getECOs: (status) => client.get(`${API_BASE_URL}/ecos`, { params: { status } }),
    createECO: (formData) => client.post(`${API_BASE_URL}/ecos`, formData, { headers: { 'Content-Type': undefined } }),
    approveECO: (ecoId) => client.post(`${API_BASE_URL}/ecos/${ecoId}/approve`),

    // ═══════════════════════════════════════════════════════════════════════════
    // HISTORY
    // Response: getHistory → [...activity entries] for the project
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ActivityFeed (project-level activity timeline)
    getHistory: (projectId) => client.get(`${API_BASE_URL}/product-structure/history`, { params: { project_id: projectId } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // ENGINEERING (AI-powered analysis)
    // Response: analyzeDesign → { analysis: "..." }, generateSpecs → { specs: "..." }
    //           processDWG → { extracted_data: {...} }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (not directly referenced in current components — backend-triggered or future UI)
    analyzeDesign: (data) => client.post(`${API_BASE_URL}/engineering/analyze`, data),
    generateSpecs: (data) => client.post(`${API_BASE_URL}/engineering/generate-specs`, data),
    processDWG: (filePath, s3Url) => client.post(`${API_BASE_URL}/engineering/process-dwg`, { file_path: filePath, s3_url: s3Url }),

    // ═══════════════════════════════════════════════════════════════════════════
    // CAD AGENT (Autodesk Platform Services integration)
    // Response: getCADViewerToken → { access_token, expires_in }
    //           getCADDrawings → [...drawings], getCADDrawing → single drawing
    //           getCADJobStatus → { status, progress, ... }
    //           translateCADDrawing → { job_id, ... }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (CAD viewer token is fetched by APSViewer, CADViewerView via client directly)
    getCADViewerToken: () => client.get(`${API_BASE_URL}/cad-agent/viewer/token`),
    // Called by: CADModelsView, UnifiedCADView (drawing list for a project)
    getCADDrawings: (projectId) => client.get(`${API_BASE_URL}/cad-agent/drawings`, { params: { project_id: projectId } }),
    getCADDrawing: (drawingId) => client.get(`${API_BASE_URL}/cad-agent/drawings/${drawingId}`),
    createCADDrawing: (data) => client.post(`${API_BASE_URL}/cad-agent/drawings`, data),
    deleteCADDrawing: (drawingId) => client.delete(`${API_BASE_URL}/cad-agent/drawings/${drawingId}`),
    getCADJobStatus: (jobId) => client.get(`${API_BASE_URL}/cad-agent/jobs/${jobId}/status`),
    getCADDrawingJobs: (drawingId) => client.get(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/jobs`),
    translateCADDrawing: (drawingId) => client.post(`${API_BASE_URL}/cad-agent/translate/${drawingId}`),
    getCADTranslationStatus: (drawingId) => client.get(`${API_BASE_URL}/cad-agent/translate/${drawingId}/status`),

    // ═══════════════════════════════════════════════════════════════════════════
    // CAD IMPORT — import an existing document into the CAD viewer
    // Side-effect: creates a CAD drawing record linked to the document and
    //   triggers a translation job. The document must already exist.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: CADModelsView, UnifiedCADView (import action on a document row)
    importDocumentToCAD: (documentId) => client.post(`${API_BASE_URL}/cad-agent/import/from-document/${documentId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENT ANALYSIS WITH AI
    // Can analyze either a document (pass documentId) or a requirement attachment (pass requirementId).
    // Response: { analysis: "...", analysis_type: "summary"|"risks"|"custom"|... }
    // Timeout: 120s (overrides client default — same value, but explicit).
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: RequirementsView (AI analysis of requirement attachments)
    analyzeDocument: ({ documentId, requirementId, analysisType = 'summary', language = 'en', customPrompt = null }) => client.post(
        `${API_BASE_URL}/documents/analyze`,
        {
            document_id: documentId || null,
            requirement_id: requirementId || null,
            analysis_type: analysisType,
            language: language,
            custom_prompt: customPrompt
        },
        { timeout: 30000 }  // Returns 202 immediately; frontend polls for result
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // ORCHESTRATOR (engineering lifecycle pipeline builder)
    // ═══════════════════════════════════════════════════════════════════════════
    getUserProfile: () => client.get(`${API_BASE_URL}/auth/me`),
    completeOnboarding: () => client.put(`${API_BASE_URL}/auth/me`, { onboarding_completed: true }),
    updateOnboardingProgress: (progress) => client.put(`${API_BASE_URL}/auth/me`, { onboarding_progress: progress }),
    getLifecycleStages: () => client.get(`${API_BASE_URL}/orchestrator/stages`),
    getStageTools: (stageId) => client.get(`${API_BASE_URL}/orchestrator/stages/${stageId}/tools`),
    getOrchestrationStatus: (runId) => client.get(`${API_BASE_URL}/orchestrator/status/${runId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENT GENERATION (UC1 — AI-generated engineering documents)
    // Response: generateDocument → { generated_doc_id, status: "generating" } (fire-and-forget)
    //           getPendingDocumentReviews → [...pending reviews]
    //           getGeneratedDocument → single generated document with content
    //           submitDocumentReview → { status: "approved"|"rejected" }
    //           getDocumentTemplates → [...templates]
    // Side-effect: submitDocumentReview with approval auto-creates a document record.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: DocumentsView (generate document action)
    generateDocument: (data) => client.post(`${API_BASE_URL}/document-generation/generate`, data, { timeout: 60000 }),
    // Called by: DocumentsView, DocumentGenerationView (pending reviews panel)
    getPendingDocumentReviews: (projectId) => client.get(
        `${API_BASE_URL}/document-generation/pending`,
        { params: projectId ? { project_id: projectId } : {} }
    ),
    // Called by: DocumentsView, DocumentGenerationView (load generated doc content for review)
    getGeneratedDocument: (id) => client.get(`${API_BASE_URL}/document-generation/${id}`),
    // Called by: DocumentsView, DocumentGenerationView (approve/reject/request-changes)
    submitDocumentReview: (id, review) => client.post(`${API_BASE_URL}/document-generation/${id}/review`, review),
    // Called by: (change impact analysis between document versions)
    analyzeChangeImpact: (sourceId, oldVersion, newVersion) => client.get(
        `${API_BASE_URL}/document-generation/impact/${sourceId}`,
        { params: { old_version_id: oldVersion, new_version_id: newVersion } }
    ),
    // Called by: DocumentsView, DocumentGenerationView (template picker in generate dialog)
    getDocumentTemplates: (type) => client.get(
        `${API_BASE_URL}/document-generation/templates`,
        { params: type ? { document_type: type } : {} }
    ),
    // Called by: (admin template management — not directly referenced in current components)
    createDocumentTemplate: (template) => client.post(`${API_BASE_URL}/document-generation/templates`, template),

    // ═══════════════════════════════════════════════════════════════════════════
    // REPORTS (AI-Generated Documents — read-only listing)
    // Response: getReports → [...reports], getLinkedReports → [...reports linked to source]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ReportsView (report listing by project/branch)
    getReports: (projectId, branchId) => client.get(
        `${API_BASE_URL}/document-generation/reports`,
        { params: { project_id: projectId, branch_id: branchId } }
    ),
    // Called by: FileDetailDrawer (shows reports linked to a source document)
    getLinkedReports: (sourceDocumentId) => client.get(
        `${API_BASE_URL}/document-generation/reports/linked/${sourceDocumentId}`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // ACTIVITY LOGS
    // Response: getActivities → [...activity log entries]
    //           getProjectActivities → [...activity log entries for a project]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ActivityContext (global activity polling)
    getActivities: (limit = 50, resourceType = null, action = null, search = null) => client.get(
        `${API_BASE_URL}/activities`,
        { params: { limit, resource_type: resourceType, action, search } }
    ),
    // Called by: ActivityFeed (project-scoped activity feed with search)
    getProjectActivities: (projectId, limit = 50, resourceType = null, action = null, search = null) => client.get(
        `${API_BASE_URL}/projects/${projectId}/activities`,
        { params: { limit, resource_type: resourceType, action, search } }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // CAD EXPORT (DWG → PDF/DWF via APS Model Derivative)
    // Response: exportDrawing → { status: "started", format, drawing_id }
    //           getExportStatus → { status: "complete"|"inprogress"|"failed", download_url?, filename? }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: CADViewerArea (Export button)
    exportDrawing: (drawingId, format) => client.post(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/export`, { format }),
    getExportStatus: (drawingId, format) => client.get(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/export/status`, { params: { format } }),
    // Called by: ComplianceView Dashboard (summary cards without loading all drawings)
    getDrawingsSummary: (projectId) => client.get(`${API_BASE_URL}/cad-agent/drawings/summary`, { params: { project_id: projectId } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROJECT MILESTONES
    // Response: getMilestones → [...milestones], createMilestone → milestone object
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: MilestonesPanel, AnalyticsView
    getMilestones: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/milestones`),
    createMilestone: (projectId, data) => client.post(`${API_BASE_URL}/projects/${projectId}/milestones`, data),
    updateMilestone: (projectId, milestoneId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/milestones/${milestoneId}`, data),
    deleteMilestone: (projectId, milestoneId) => client.delete(`${API_BASE_URL}/projects/${projectId}/milestones/${milestoneId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROJECT ANALYTICS
    // Response: getProjectBrief → { project, requirements, documents, ... }
    //           getProjectProgression → { commits_per_week, doc_versions_per_week, ... }
    //           getProjectRisks → { failed_requirements, stale_branches, ... }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: AnalyticsView (Project Brief tab)
    getProjectBrief: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/brief`),
    // Called by: AnalyticsView (Progression tab)
    getProjectProgression: (projectId, weeks = 12) => client.get(
        `${API_BASE_URL}/projects/${projectId}/analytics/progression`,
        { params: { weeks } }
    ),
    // Called by: AnalyticsView (Risks tab)
    getProjectRisks: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/risks`),
    // Called by: AnalyticsView (Scorecard tab)
    getProjectScorecard: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/scorecard`),
    // Called by: AnalyticsView (Intelligence tab)
    getEngineeringIntelligence: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/intelligence`),
    // Called by: AnalyticsView (AI Health Summary tab)
    getProjectHealthSummary: (projectId, force = false) => client.get(
        `${API_BASE_URL}/projects/${projectId}/analytics/health-summary`,
        { params: { force } }
    ),
    // Called by: AnalyticsView (Gate Review Report)
    getGateReviewReport: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/gate-review`),
    exportGateReviewReport: (projectId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/analytics/gate-review`,
        { params: { format: 'xlsx' }, responseType: 'blob' }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // BOM EXTRACTION (AI-powered Bill of Materials from CAD drawings)
    // Response: saveBOM → { message, ... }, exportBOM → Blob (file download)
    // extractBOM: starts async BOM extraction, returns 202 + job_id
    extractBOM: (drawingId) => client.post(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/extract-bom`),
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: BOMReviewDialog (save user-edited BOM back to DB)
    saveBOM: (drawingId, bomItems) => client.put(
        `/api/v1/cad-agent/drawings/${drawingId}/bom`,
        { bom_items: bomItems }
    ),
    // Called by: BOMReviewDialog (download Excel/CSV export)
    exportBOM: (drawingId, format = 'xlsx') => client.get(
        `/api/v1/cad-agent/drawings/${drawingId}/bom/export`,
        { params: { format }, responseType: 'blob' }
    ),
    // Called by: BOMReviewDialog (retrieve previously extracted BOM)
    getBOM: (drawingId) => client.get(`/api/v1/cad-agent/drawings/${drawingId}/bom`),

    // ═══════════════════════════════════════════════════════════════════════════
    // COMPLIANCE CHECKING
    // checkCompliance uses native fetch() for NDJSON streaming — NOT Axios.
    //   The caller handles streaming (see CADFileDetailDrawer Compliance tab).
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: CADFileDetailDrawer (retrieve saved compliance result)
    getCompliance: (drawingId) => client.get(`/api/v1/cad-agent/drawings/${drawingId}/compliance`),
    // Called by: ComplianceView Dashboard (manual pass/fail/warning override)
    setManualComplianceStatus: (drawingId, status) =>
        client.patch(`/api/v1/cad-agent/drawings/${drawingId}/compliance-status`, { status }),
    // Called by: ComplianceView Dashboard (assign requirements/standards to a drawing)
    setComplianceAssigned: (drawingId, requirementIds, standardIds) =>
        client.patch(`/api/v1/cad-agent/drawings/${drawingId}/compliance-assigned`, {
            requirement_ids: requirementIds, standard_ids: standardIds,
        }),
    // Called by: CADFileDetailDrawer (download Excel/PDF export)
    exportCompliance: (drawingId, format = 'xlsx') => client.get(
        `/api/v1/cad-agent/drawings/${drawingId}/compliance/export`,
        { params: { format }, responseType: 'blob' }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // COMPLIANCE STANDARDS
    // ═══════════════════════════════════════════════════════════════════════════
    getComplianceStandards: (projectId, { search, limit, offset, branch_id } = {}) => client.get(`${API_BASE_URL}/compliance/standards`, { params: { project_id: projectId, branch_id, search, limit, offset } }),
    createComplianceStandard: (formData) => client.post(`${API_BASE_URL}/compliance/standards`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DropZoneUploader (drag-drop in ComplianceView creates a ComplianceStandard per file)
    createComplianceStandardFromFile: (formData) => client.post(`${API_BASE_URL}/compliance/standards`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DropZoneUploader (drag-drop in CAD view — uses the regular file upload which auto-categorizes as 'cad')
    uploadCADDrawing: (formData) => client.post(`${API_BASE_URL}/files/upload`, formData, { headers: { 'Content-Type': undefined } }),
    updateComplianceStandard: (standardId, data) => client.put(`${API_BASE_URL}/compliance/standards/${standardId}`, data),
    deleteComplianceStandard: (standardId) => client.delete(`${API_BASE_URL}/compliance/standards/${standardId}`),
    getComplianceStandardDownloadUrl: (standardId) => client.get(`${API_BASE_URL}/compliance/standards/${standardId}/download`),
    // Called by: ComplianceStandardDetailDrawer (history tab)
    getStandardVersions: (standardId) => client.get(`${API_BASE_URL}/compliance/standards/${standardId}/versions`),
    // Called by: ComplianceStandardDetailDrawer (compare tab)
    compareStandardVersions: (v1Id, v2Id) => client.post(`${API_BASE_URL}/compliance/standards/versions/compare`, {
        version1_id: v1Id,
        version2_id: v2Id,
        entity_type: 'compliance_standard'
    }),

    // COMPLIANCE TRACEABILITY MATRIX
    getComplianceMatrix: (projectId) => client.get(`${API_BASE_URL}/compliance/matrix`, { params: { project_id: projectId } }),
    getComplianceCoverage: (projectId) => client.get(`${API_BASE_URL}/compliance/coverage`, { params: { project_id: projectId } }),
    getComplianceHistory: (projectId, drawingId) => client.get(`${API_BASE_URL}/compliance/history`, { params: { project_id: projectId, drawing_id: drawingId } }),
    getComplianceAuditLog: (projectId, limit = 50, offset = 0) => client.get(`${API_BASE_URL}/compliance/audit-log`, { params: { project_id: projectId, limit, offset } }),
    exportComplianceMatrix: (projectId) => client.get(`${API_BASE_URL}/compliance/matrix/export`, { params: { project_id: projectId }, responseType: 'blob' }),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROJECT KNOWLEDGE BASE
    // ═══════════════════════════════════════════════════════════════════════════
    getProjectKB: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/kb`),
    removeKBDocument: (projectId, documentId) => client.delete(`${API_BASE_URL}/projects/${projectId}/kb/documents/${documentId}`),
    toggleKBContext: (projectId, documentId) => client.patch(`${API_BASE_URL}/projects/${projectId}/kb/documents/${documentId}/context`),
    searchKB: (projectId, query, topK = 8) => client.post(`${API_BASE_URL}/projects/${projectId}/kb/search`, { query, top_k: topK }),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROCESSING JOBS (async extraction pipeline)
    // Response: getJobStatus → { job_id, status, progress, stage, ... }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: useParallelUpload hook (poll extraction progress after upload returns 202)
    getJobStatus: (jobId) => client.get(`${API_BASE_URL}/processing/jobs/${jobId}`),
    getBatchJobStatus: (jobIds) => client.post(`${API_BASE_URL}/processing/jobs/batch-status`, { job_ids: jobIds }),
    getActiveJobs: (projectId) => client.get(`${API_BASE_URL}/processing/projects/${projectId}/active`),

    // ═══════════════════════════════════════════════════════════════════════════
    // CHAT HISTORY
    // Response: getChatHistory → { messages: [...] }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ChatContainer (load persisted messages on mount)
    getChatHistory: (sessionId) => client.get(`${API_BASE_URL}/chat/history/${sessionId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // PRIVACY & GDPR
    // Response: exportUserData → file download (JSON), getDataRetentionInfo → { policies: [...] }
    //           getConsentStatus → { consents: {...} }
    // Side-effect: requestAccountDeletion starts an irreversible deletion process
    //   (unless cancelled within the grace period via cancelAccountDeletion).
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (SettingsPage privacy tab — not using api.js, uses client directly)
    // These endpoints exist for future integration or direct usage.
    exportUserData: () => client.get(`${API_BASE_URL}/privacy/export`),
    getDataRetentionInfo: () => client.get(`${API_BASE_URL}/privacy/data-retention`),
    requestAccountDeletion: (confirmation, reason, feedback) => client.delete(
        `${API_BASE_URL}/privacy/account`,
        { data: { confirmation, reason, feedback } }
    ),
    cancelAccountDeletion: () => client.post(`${API_BASE_URL}/privacy/account/cancel-deletion`),
    getConsentStatus: () => client.get(`${API_BASE_URL}/privacy/consent`),
    updateConsent: (consent) => client.put(`${API_BASE_URL}/privacy/consent`, consent),

    // ═══════════════════════════════════════════════════════════════════════════
    // SHAREPOINT / ONEDRIVE (Microsoft 365 file connector)
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: SharePointPicker (check connection status)
    getSharePointStatus: () => client.get(`${API_BASE_URL}/sharepoint/status`),
    // Called by: SharePointPicker (list available drives)
    getSharePointDrives: () => client.get(`${API_BASE_URL}/sharepoint/drives`),
    // Called by: SharePointPicker (browse folder contents)
    browseSharePoint: (driveId, folderId = 'root') => client.get(`${API_BASE_URL}/sharepoint/browse`, { params: { drive_id: driveId, folder_id: folderId } }),
    // Called by: SharePointPicker (import file to EIS project)
    importFromSharePoint: (data) => client.post(`${API_BASE_URL}/sharepoint/import`, data),
    // Called by: Settings (disconnect Microsoft 365)
    disconnectSharePoint: () => client.delete(`${API_BASE_URL}/sharepoint/disconnect`),

};
